import {
    ApproverDetailsPersonalInfo,
    ClaimStatusWithNestedStatus,
    ClaimStatus,
    ApproveStatus,
} from "./claim";
import {
    InvoiceStatus
} from "./invoice";
import { PaymentOrderStatus, PaymentStatus } from "./payment";

import {
    GenericState,
    LinearId,
    MOCK_ARCHITECT,
    MOCK_CONTRACTOR,
    MOCK_DEVELOPER,
    PartyDetail,
    UUID,
} from "./common";

type ProjectProps = {
    name: string;
    id: string;
    developerDetails: PartyDetail;
    mainContractorDetails: PartyDetail[];
    architectDetails: PartyDetail;
    claimLinearIds: Map<string, LinearId[]>;
    claimApprovers: ApproverDetailsPersonalInfo[];
    bankAccounts: string[];
    paymentOrderApprovers: ApproverDetailsPersonalInfo[];
};

export type ProjectState = GenericState<ProjectProps, ProjectStatus>;

export enum ProjectStatus {
    CREATED = "CREATED",
    PENDING_DELETE = "PENDING_DELETE",
}

export type ProjectStateWithNestedStatus = {
    projectState: BesuProjectState;
    claimStatuses: ClaimStatusWithNestedStatus[];
    projectId? :string,
    name? :string,
};

export type BesuProjectParty = {
    accountName: string;
    displayName: string;
    wallet?: string | null;
};

export type BesuProjectContractorClaims = {
    contractorAccountName: string;
    claimIds: string[];
};

export type BesuProjectClaimStatusWithNestedStatus = ClaimStatusWithNestedStatus & {
    claimId: string;
};

export type BesuProjectState = {
    referenceId: UUID;
    externalProjectId: string;
    name: string;
    status: ProjectStatus;
    createdAt: string;
    updatedAt: string;
    developer?: BesuProjectParty | null;
    mainContractors: BesuProjectParty[];
    architect?: BesuProjectParty | null;
    claimIds: string[];
    claimReferenceIds: UUID[];
    claimsByContractor: BesuProjectContractorClaims[];
    bankAccountRefs: string[];
    claimApprovers: ApproverDetailsPersonalInfo[];
    paymentOrderApprovers: ApproverDetailsPersonalInfo[];
    claimStatuses: BesuProjectClaimStatusWithNestedStatus[];
    claimCountByContractorList?: BesuClaimCountByContractorPayload[]
};

export type useProjectListResponse = {
    items?: BesuProjectState[];
    pageSize: number;
    page: number;
    maxLinkedPages: number;
    firstElementOnPage: number;
    lastElementOnPage: number;
    nrOfElements: number;
    lastLinkedPage: number;
    firstLinkedPage: number;
    pageCount: number;
    firstPage: boolean;
    lastPage: boolean;
    pageList?: ProjectStateWithNestedStatus[];
    refreshDate: string;
    sort?: {
        property: string;
        ignoreCase: boolean;
        ascending: boolean;
        toggleAscendingOnProperty: boolean;
    };
    source: unknown[];
};

export type BesuClaimCountByContractorPayload = {
    contractorWallet: string;
    claimCount: number;
};

export type ProjectStateWithTxn = {
    state: BesuProjectState;
    totalAmount: number;
    trxnCount: number;
};

export enum ProjectUserRole {
    ADMIN = "ADMIN",
    CLAIM_APPROVER = "CLAIM_APPROVER",
    FINANCE_DEPARTMENT = "FINANCE_DEPARTMENT",
    HSBC_OPERATOR = "HSBC_OPERATOR",
    MAIN_CONTRACTOR = "MAIN_CONTRACTOR",
    PAYMENT_ORDER_APPROVER = "PAYMENT_ORDER_APPROVER",
    PROJECT_OFFICER = "PROJECT_OFFICER",
    SUPER_ADMIN = "SUPER_ADMIN",
}

export const MOCK_PROJECT: ProjectState = {
    linearId: {
        id: "some-uuid-1",
    },
    createdAt: "2021-11-02T04:18:43.299Z",
    status: ProjectStatus.CREATED,
    lastChangedTime: "2021-11-02T04:18:43.299Z",
    props: {
        name: "Diamond House",
        id: "027837624",
        developerDetails: MOCK_DEVELOPER,
        mainContractorDetails: MOCK_CONTRACTOR,
        architectDetails: MOCK_ARCHITECT,
        claimLinearIds: new Map([["contractor", [{ id: "some-uuid" }]]]),
        claimApprovers: [
            {
                roleName: "",
                firstName: "",
                lastName: "",
                email: "",
                userProfileName: "",
            },
        ],
        bankAccounts: ["233-434-342", "344-555-223"],
        paymentOrderApprovers: [
            {
                roleName: "",
                firstName: "",
                lastName: "",
                email: "",
                userProfileName: "",
            },
        ],
    },
    comments: [],
};

export const MOCK_BESU_PROJECT: BesuProjectState = {
    referenceId: "b601b28e-4d53-8ba6-be58-9bfadec40d2f",
    externalProjectId: "304",
    name: "US on it",
    status: ProjectStatus.CREATED,
    createdAt: "2026-05-31T12:28:31.817Z",
    updatedAt: "2026-05-31T12:28:31.817Z",
    developer: {
        accountName: "CNConstructionCordaAccount0001",
        displayName: "CNConstructionCordaAccount0001",
    },
    mainContractors: [
        {
            accountName: "testShellyCordaAcc",
            displayName: "testShellyCordaAcc",
        },
        {
            accountName: "testSylenCordaAcc",
            displayName: "testSylenCordaAcc",
        }
        ],
    architect: null,
    claimIds: [
        "3b32935c-fd77-4d67-8b41-5fb2dd7d7c17",
        "2933507e-c17e-4ae2-939c-164e6f3849fd",
    ],
    claimReferenceIds: ["some-uuid"],
    claimsByContractor: [
        {
            contractorAccountName: "testShellyCordaAcc",
            claimIds: [
                "3b32935c-fd77-4d67-8b41-5fb2dd7d7c17",
            ]
        },
        {
            contractorAccountName: "testSylenCordaAcc",
            claimIds: [
                "2933507e-c17e-4ae2-939c-164e6f3849fd",
            ]
        }
    ],
    bankAccountRefs: ["testBankAcc"],
    claimApprovers:
    [
        {
            "roleName": "k2",
            "firstName": "Approver-claim ",
            "lastName": "005",
            "party": {
                "account": {
                    "name": "CNConstructionCordaAccount0001",
                    "host": "CN=CK Asset Holdings Limited Shared Node, OU=Developer Shared Node, O=CK Asset Holdings Limited, L=Singapore, C=SG",
                    "identifier": {
                        "id": "582b1927-6207-490f-a359-107458e7cf03"
                    },
                    "linearId": {
                        "id": "582b1927-6207-490f-a359-107458e7cf03"
                    },
                    "participants": [
                        "CN=CK Asset Holdings Limited Shared Node, OU=Developer Shared Node, O=CK Asset Holdings Limited, L=Singapore, C=SG"
                    ]
                },
                "party": "net.corda.core.identity.AnonymousParty$Companion@52fe547b"
            },
            "email": "shellyyaapporover01@mail.com",
            "userProfileName": "Audit company"
        }
    ],
    paymentOrderApprovers:
    [
        {
            "roleName": "k1",
            "firstName": "approver-payment",
            "lastName": "006",
            "email": "shellyappay@mail.com",
            "userProfileName": "Audit company"
        }
    ],
    claimStatuses: [
       {
           claimId: "test001",
           "claimStatus": ClaimStatus.APPROVED,
           "invoiceStatuses": [
               {
                   "invoiceStatus": InvoiceStatus.FINANCE_APPROVED,
                   "paymentInfo": {
                       "paymentOrderApprovers": [
                           {
                               "details": {
                                   "roleName": "k1",
                                   "firstName": "approver-payment",
                                   "lastName": "006",
                                   "email": "shellyappay@mail.com",
                                   "userProfileName": "Audit company"
                               },
                               "actionTimestamp": "2026-06-18T09:56:23.337Z",
                               "status": ApproveStatus.APPROVED,
                           }
                       ],
                       "paymentOrderStatus": PaymentOrderStatus.ALL_APPROVED,
                       "paymentStatus": PaymentStatus.SUBMITTED
                   }
               }
           ]
       }
   ],
};

export const MP_MOCK_PROJECT: any = {
    state: {
        data: {
            props: {
                id: "TKH2L23",
                name: "TKH Renovation",
                claimLinearIds: new Map(),
                mainContractorDetails: [
                    {
                        name: "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
                        party: "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
                    },
                    {
                        name: "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
                        party: "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
				},
                ],
                claimApprovers: [
                {
                        roleName: "Claim Approver",
                        firstName: "CMF",
                        lastName: "Claim Approver 01",
                        party: "CN=CK Development Limited, OU=Developer, O=CryptoBLK Limited, L=Singapore, C=SG",
                        email: "monachen139+CMFclaimapprover01@gmail.com",
                        userProfileName: "Approver Entity",
				},
                ],
                paymentOrderApprovers: [
                    {
                        roleName: "Payment Order Approver",
                        firstName: "CMF",
                        lastName: "Payment Order Approver 01",
                        party: null,
                        email: "monachen139+CMFpayment-order-approver01@gmail.com",
                        userProfileName: "Approver Entity",
			},
                ],
                developerDetails: {
                    name: "CK Development Limited",
                    party: "CN=CK Development Limited, OU=Developer, O=CryptoBLK Limited, L=Singapore, C=SG",
			},
                bankAccounts: [],
            },
            linearId: {
                externalId: null,
                id: "c3c8604c-6eae-4183-a61e-c6cf87addeac",
            },
            participants: [
                "CN=CK Development Limited, OU=Developer, O=CryptoBLK Limited, L=Singapore, C=SG",
                "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
            ],
            status: "UPDATED",
            lastChangedParty:
                "CN=China Construction South Pacific Dev, OU=Contractor, O=China Construction South Pacific Development, L=Singapore, C=SG",
            lastIsRevised: true,
            command: {},
            commandString:
                "io.cryptoblk.topazelite.contract.ProjectContract$Command$AddClaim@ff61d196",
            version: 1,
            lastChangedTime: "2024-02-04T02:27:43.964Z",
            createdAt: "2024-01-31T08:47:28.759Z",
        },
        contract: "io.cryptoblk.topazelite.contract.ProjectContract",
        notary: "CN=Notary, O=Doorman, L=HongKong, C=CN",
        encumbrance: null,
        constraint: {
            key: {
                modulus: 19787247740534100219988907851214857845218786359336452421213728033403777300808360799437894650413910284236439117244441244808535015343591668853419196699174501599448102070155036980168076834874293654303023583091385432883779215383090737219494359054217916816117716177196942330418263051684707155351941483324717631900213644516620058968235175898249944714554850938200918454316127655139473302905430301682564161869293203138739336961424100632026748851763729261609121760930338221482374311814925197798769782314904211581909090143837379721815706556782492571027284221984999150303803277441418549682819573099860785757438348583138511499251,
                publicExponent: 65537,
                algorithm: "RSA",
                format: "X.509",
                encoded:
                    "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAnL7FGxfZ2OwL/Am0AkkFmpz+RI3hdlmZ4w/hdMvHbVFXTfzwk5GUTDq4D0kQBzQ7FmBTyPhrvM5oLgs6ziPyIBBxCvuO9WwYzP/bod4yzFzYi7EoRcUTY24a6+4E0G1eXptrykEWsaTkG681sbycRSOouNEzp8bH2szWAprbZQeU0sX7h+41YMCrHZSXkWrC/KymZ2tFNw+XddgKqnCOGTr/uM7V/HK4oXho5XN7lG0hE9A/igJboqbkC57jQxOCre3Kv3CTdoSbR7dygO6C3fDfToO2Q/gZZJDYo7eVYc4QpMYfu4P2W+zMRH/E62e7iq30EDjZlhSttllvBuMv8wIDAQAB",
                params: null,
			},
		},
	},
    ref: "A091E383C8814B9E8DFCEE42763C98543773D04B554625680992946D9F33F0D5(1)",
};

export const MOCK_PROJECT_LIST: useProjectListResponse = {
    pageSize: 1,
    page: 1,
    maxLinkedPages: 1,
    firstElementOnPage: 1,
    lastElementOnPage: 1,
    nrOfElements: 1,
    lastLinkedPage: 1,
    firstLinkedPage: 1,
    pageCount: 1,
    firstPage: true,
    lastPage: true,
    items: [
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        MOCK_BESU_PROJECT,
        ],
    pageList: [
    
    ],
    refreshDate: "",
    sort: {
        property: "",
        ignoreCase: true,
        ascending: false,
        toggleAscendingOnProperty: false,
    },
    source: [],
};

export const MS_MOCK_PROJECT_LIST: ProjectStateWithNestedStatus[] =[

];
    
export const MOCK_PROJECT_LIST_WITH_TXN: ProjectStateWithTxn[] =
    MOCK_PROJECT_LIST.pageList!.map((it) => ({
        state: it.projectState,
        totalAmount: 12200000,
        trxnCount: 3,
    }));
